import { TFunction } from "i18next";
import { FormFieldType } from "@slootsantos/certain-forms/dist/types";

import { ImageType } from "components/Documents/types";
import { RiskGroup } from "pages/Risk/hooks/useRiskGroups";
import {
  Ex,
  buildRiskFormData,
  handleRiskGroupChange,
} from "components/Risk/formdata";

// `buildRiskFormData` only shallow-copies the module-level `formData`
// singleton in components/Risk/formdata.tsx: the sections array, the section
// objects and every field object are SHARED with that singleton. Mutating its
// result (stripping fields, setting `count`) would therefore leak into the
// status-quo risk editor. This clone creates a new sections array, new
// section objects and new fields arrays with shallow-cloned field objects —
// shallow on purpose, so `customRenderOverride` function references are
// preserved (option arrays are only ever replaced wholesale in formdata.tsx,
// never mutated in place, so sharing them is safe).
const cloneFormStructure = (form: Ex): Ex => ({
  ...form,
  sections: form.sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) => ({ ...field })),
  })),
});

export const buildAIDraftFormData = (
  riskGroups: RiskGroup[],
  translator: TFunction,
  groupId: string,
  mitigationCount: number
): Ex => {
  const cloned = cloneFormStructure(
    buildRiskFormData(riskGroups, translator, groupId)
  );

  // No risk resource_id exists before acceptance, so the risk_image upload
  // cannot work in the AI draft editor. The detail page renders an i18n hint
  // (risks.ai.detail.imageHint) pointing to the normal editor post-accept.
  cloned.sections[0].fields = cloned.sections[0].fields.filter(
    (field) =>
      !(
        field.type === FormFieldType.upload &&
        field.name === ImageType.riskImage
      )
  );

  // Replicates Edit.tsx: `formdata.sections[1].count = risk.mitigations?.length || 1`
  // — but on the private clone, never on the shared singleton.
  cloned.sections[1].count = mitigationCount || 1;

  return cloned;
};

export const handleAIDraftGroupChange = (
  selectedRiskGroup: RiskGroup,
  translator: TFunction,
  currentForm: Ex
): Ex => {
  // ALWAYS pass the stripped clone as the third argument: without it,
  // `handleRiskGroupChange` falls back to the module-level `formData`
  // singleton, and the stripped upload field would resurrect on group change
  // (and the change would mutate the singleton shared with the status-quo
  // editor).
  return handleRiskGroupChange(selectedRiskGroup, translator, currentForm);
};
